import {
  LEVEL,
  LEVEL_CRITICAL,
  LEVEL_DEBUG,
  LEVEL_ERROR,
  LEVEL_INFO,
  LEVEL_WARN,
  useRollbar,
  useRollbarPerson,
} from '@rollbar/react';
import { useSelector } from 'react-redux';
import Rollbar from 'rollbar';

import { selectDataUser } from 'connect/selectors';

const environment = (() => {
  if (process.env.ENV_STAGING === 'true') {
    return 'staging';
  } else if (process.env.ENV_DEMO === 'true') {
    return 'demo';
  }
  return process.env.NODE_ENV;
})();

const getConsoleFunc = (level: LEVEL) => ({
  [LEVEL_DEBUG]: console.debug,
  [LEVEL_INFO]: console.info,
  [LEVEL_WARN]: console.warn,
  [LEVEL_ERROR]: console.error,
  [LEVEL_CRITICAL]: console.error,
}[level]);

const initializeRollbar = () => Rollbar.init({
  accessToken: '0714bc4786cd49a89091a3f2d860ef33',
  captureUncaught: true,
  captureUnhandledRejections: true,
  // Called before payload is sent to Rollbar, does not mutate the payload
  // Note - `verbose` does a similar thing but only logs, so we don't get the stack trace
  onSendCallback: (isUncaught: boolean, args: Rollbar.LogArgument[], item: Rollbar.Payload) => {
    const consoleFunc = getConsoleFunc(item.level);
    consoleFunc(
      isUncaught ? 'Uncaught' : 'Caught',
      `${item.level.toUpperCase()}:`,
      ...args,
    );
  },
  payload: {
    environment,
  },
  transform: (payload: Rollbar.Payload) => {
    // Add the FullStory session URL to the Rollbar payload
    if (window.FS && window.FS.getCurrentSessionURL) {
      payload.fullstorySessionURL = window.FS.getCurrentSessionURL(true);
    }

    // Add the Heap User ID and Session ID to the Rollbar payload
    if (window.heap && window.heap.userId) {
      payload.heapUserId = window.heap.userId;
      payload.heapSessionId = window.heap.getSessionId();
    }
  },
});

let rollbarInstance: Rollbar | undefined;

const initialize = () => {
  if (rollbarInstance) {
    rollbarInstance.warning('Rollbar already initialized');
    return rollbarInstance;
  }

  rollbarInstance = initializeRollbar();
  window.Rollbar = rollbarInstance;
  return rollbarInstance;
};

/**
 * Gets the Rollbar instance and sets the Rollbar person
 */
export const useLogger = () => {
  const user = useSelector(selectDataUser);
  const rollbarPerson = user?.id ? { id: user.id } : {};
  useRollbarPerson(rollbarPerson);

  return useRollbar();
};

const debug = (...args: any[]) => {
  rollbarInstance?.debug(...args);
};
const info = (...args: any[]) => {
  rollbarInstance?.info(...args);
};
const warning = (...args: any[]) => {
  rollbarInstance?.warning(...args);
};
const error = (...args: any[]) => {
  rollbarInstance?.error(...args);
};
const critical = (...args: any[]) => {
  rollbarInstance?.critical(...args);
};

export default {
  initialize,
  debug,
  info,
  warning,
  error,
  critical,
};
